import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../ui/Modal';
import Spinner from '../../ui/Spinner';
import { Download, Icon, Share2 } from '../../ui/Icon';
import { toast } from '../../ui/Toast';
import { photosApi } from '../../../api/photos.api';
import { itineraryApi } from '../../../api/itinerary.api';
import { directionsApi, type ItineraryRouteDay } from '../../../api/directions.api';
import { useDateLocale } from '../../../utils/dateLocale';
import type { ItineraryDay, Trip, TripPhoto } from '../../../types';
import { tripsApi } from '../../../api/trips.api';
import { buildRouteShape } from './routeShape';
import { decodeImageFile } from './decodeImageFile';
import { buildStickerStats, fileSlug, formatTripDates } from './stickerData';
import {
  ensureStickerFonts, LIGHT_GRADIENTS, STICKER_GRADIENTS, STICKER_HEIGHT, type StickerSpec,
} from './stickerSpec';
import { clampFraming, DEFAULT_FRAMING, type Framing } from './framing';
import { paintSticker } from './stickerRenderer';
import StickerControls, { type BackgroundChoice } from './StickerControls';
import { useStickerBackground, type LoadedBackground } from './useStickerBackground';
import { tooLargeMessage } from '../../../utils/uploadLimits';

const WORDMARK = 'Gromly';
const NO_DAYS: ItineraryDay[] = [];
const NO_IMAGE: LoadedBackground = { image: null, width: 0, height: 0 };

interface Props {
  trip: Trip;
  /** The itinerary, when the caller already has it. Fetched otherwise — the
   *  dashboard opens this modal from a card, with no days loaded. */
  days?: ItineraryDay[];
  onClose: () => void;
}

export default function ShareStoryModal({ trip, days: providedDays, onClose }: Props) {
  const { t } = useTranslation();
  const dateLocale = useDateLocale();
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const [fetchedDays, setFetchedDays] = useState<ItineraryDay[] | null>(null);
  const [photos, setPhotos] = useState<TripPhoto[]>([]);
  const [choice, setChoice] = useState<BackgroundChoice>({
    kind: 'gradient',
    photoId: null,
    gradient: 'ink',
  });
  const [uploaded, setUploaded] = useState<LoadedBackground | null>(null);
  const [framing, setFraming] = useState<Framing>(DEFAULT_FRAMING);
  const drag = useRef<{ x: number; y: number; start: Framing } | null>(null);
  const [routeDays, setRouteDays] = useState<ItineraryRouteDay[] | null>(null);
  const [fontsReady, setFontsReady] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [converting, setConverting] = useState(false);

  const fetched = useStickerBackground(trip.id, choice.kind, choice.photoId);
  const background = choice.kind === 'upload' ? uploaded ?? NO_IMAGE : fetched.background;

  useEffect(() => { ensureStickerFonts().then(() => setFontsReady(true)); }, []);
  useEffect(() => { photosApi.list(trip.id).then(setPhotos).catch(() => setPhotos([])); }, [trip.id]);

  useEffect(() => {
    if (providedDays) return;
    itineraryApi.listDays(trip.id).then(setFetchedDays).catch(() => setFetchedDays([]));
  }, [providedDays, trip.id]);

  // The route is the sticker, so its geometry is always worth the Routes call —
  // server-cached 30d, the same one the map tab makes.
  useEffect(() => {
    if (routeDays !== null) return;
    directionsApi.route(trip.id).then(setRouteDays).catch(() => setRouteDays([]));
  }, [routeDays, trip.id]);

  useEffect(() => { if (fetched.failed) toast(t('trip.story.imageFailed'), 'error'); }, [fetched.failed, t]);

  const days = providedDays ?? fetchedDays ?? NO_DAYS;
  const shape = useMemo(() => buildRouteShape(days, routeDays), [days, routeDays]);

  const spec = useMemo<StickerSpec>(() => ({
    title: trip.name,
    subtitle: `${trip.destination} · ${formatTripDates(trip, dateLocale)}`,
    stats: buildStickerStats(days, shape, {
      days: t('trip.story.statDays'),
      stops: t('trip.story.statStops'),
      distance: t('trip.story.statDistance'),
    }),
    background: {
      image: background.image,
      imageWidth: background.width,
      imageHeight: background.height,
      framing,
      gradient: STICKER_GRADIENTS[choice.gradient] ?? STICKER_GRADIENTS.ink,
      tone: choice.kind === 'gradient' && LIGHT_GRADIENTS.includes(choice.gradient) ? 'light' : 'dark',
    },
    routeShape: shape,
    watermark: WORDMARK,
  }), [trip, dateLocale, days, shape, background, framing, choice, t]);

  useEffect(() => {
    if (!fontsReady || !canvasRef.current) return;
    paintSticker(canvasRef.current, spec);
  }, [fontsReady, spec]);

  const canFrame = Boolean(background.image);
  const { width: imageWidth, height: imageHeight } = background;

  // React's onWheel is passive, so it can't stop the modal scrolling while the
  // wheel zooms the photo: the listener goes on the canvas by hand.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canFrame) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setFraming(f => clampFraming(imageWidth, imageHeight, { ...f, zoom: f.zoom * Math.exp(-e.deltaY * 0.002) }));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [canFrame, imageWidth, imageHeight]);

  function startDrag(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!canFrame) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { x: e.clientX, y: e.clientY, start: framing };
  }

  function moveDrag(e: React.PointerEvent<HTMLCanvasElement>) {
    const d = drag.current;
    if (!d) return;
    // Screen pixels to sticker pixels: the preview is the 1080x1920 canvas scaled down.
    const k = STICKER_HEIGHT / e.currentTarget.getBoundingClientRect().height;
    setFraming(clampFraming(imageWidth, imageHeight, {
      ...d.start,
      panX: d.start.panX + (e.clientX - d.x) * k,
      panY: d.start.panY + (e.clientY - d.y) * k,
    }));
  }

  const endDrag = () => { drag.current = null; };

  function chooseBackground(next: BackgroundChoice) {
    setChoice(next);
    setFraming(DEFAULT_FRAMING);
  }

  async function pickFile(file: File) {
    // Only reaches the server when the browser can't decode it (HEIC), but the
    // limit is on the file the user picked either way.
    const tooLarge = tooLargeMessage(file, 'photo');
    if (tooLarge) { toast(tooLarge, 'error'); return; }
    setConverting(true);
    try {
      const image = await decodeImageFile(file, f => tripsApi.convertShareBackground(trip.id, f));

      setUploaded(current => {
        current?.image?.close();
        return { image, width: image.width, height: image.height };
      });
      setChoice(c => ({ ...c, kind: 'upload', photoId: null }));
      setFraming(DEFAULT_FRAMING);
    } catch {
      toast(t('trip.story.imageFailed'), 'error');
    } finally {
      setConverting(false);
    }
  }

  async function toPngFile(): Promise<File | null> {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/png'));
    if (!blob) return null;
    return new File([blob], `${fileSlug(trip.name)}-story.png`, { type: 'image/png' });
  }

  async function run(action: (file: File) => Promise<void> | void) {
    setExporting(true);
    try {
      const file = await toPngFile();
      if (!file) throw new Error('EXPORT_FAILED');
      await action(file);
    } catch {
      toast(t('trip.story.exportFailed'), 'error');
    } finally {
      setExporting(false);
    }
  }

  const download = () => run(file => {
    const url = URL.createObjectURL(file);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = file.name;
    anchor.click();
    URL.revokeObjectURL(url);
  });

  const share = () => run(async file => {
    // Cancelling the share sheet rejects with AbortError; that's not a failure.
    try {
      await navigator.share({ files: [file], title: trip.name });
    } catch (error) {
      if ((error as Error)?.name !== 'AbortError') throw error;
    }
  });

  // Probed with a throwaway file: `canShare` answers per file type, and only
  // phones (where the Instagram app is) accept an image.
  const canShareFile = useMemo(
    () => typeof navigator.canShare === 'function'
      && navigator.canShare({ files: [new File([], 'probe.png', { type: 'image/png' })] }),
    [],
  );

  const daysReady = Boolean(providedDays) || fetchedDays !== null;
  const busy = fetched.loading || converting || routeDays === null || !daysReady || !fontsReady;

  return (
    <Modal open onClose={onClose} title={t('trip.story.title')} size="lg">
      <div className="flex flex-col sm:flex-row gap-5">
        <div className="relative shrink-0 mx-auto">
          <canvas
            ref={canvasRef}
            className={`block h-[420px] w-auto rounded-2xl shadow-lg bg-slate-900 ${canFrame ? 'cursor-grab active:cursor-grabbing touch-none' : ''}`}
            aria-label={t('trip.story.previewAlt')}
            onPointerDown={startDrag}
            onPointerMove={moveDrag}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
          />
          {busy && (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-slate-900/40">
              <Spinner />
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col justify-between gap-5">
          <StickerControls
            photos={photos}
            choice={choice}
            onChoice={chooseBackground}
            onFile={pickFile}
            hasUpload={uploaded !== null}
          />
          {canFrame && <p className="text-xs text-slate-500 dark:text-slate-400">{t('trip.story.framingHint')}</p>}

          <div className="space-y-2">
            <div className="flex gap-2">
              <button onClick={download} disabled={exporting || busy} className="btn-primary flex-1 gap-1.5 text-xs py-2">
                <Icon as={Download} size={14} />
                {t('trip.story.download')}
              </button>
              {canShareFile && (
                <button onClick={share} disabled={exporting || busy} className="btn-secondary gap-1.5 text-xs py-2 px-3">
                  <Icon as={Share2} size={14} />
                  {t('trip.story.share')}
                </button>
              )}
            </div>
            <p className="text-2xs text-slate-400 dark:text-slate-500 text-center">{t('trip.story.hint')}</p>
          </div>
        </div>
      </div>
    </Modal>
  );
}
